/**
 * FERP Portal - Normalized Entity Types
 *
 * DRR-027: Life-Safety Enhanced Normalization Layer
 *
 * This module provides type-safe normalization for entities from heterogeneous
 * subgraphs (FHIR, Payroll, OFBiz) while preserving domain-specific type information.
 *
 * Key design decisions:
 * - Generic T preserves domain type through transformation (not `unknown`)
 * - SafetyLevel is REQUIRED, not optional (life-safety enforcement)
 * - ComplianceFrameworks enables regulatory traceability
 * - SourceSubgraph enables audit trail and subgraph-aware business logic
 */

import type { SafetyLevel } from './graphql-compat';

/**
 * Subgraph identifiers for source tracking.
 * Each subgraph in the federation has a unique identifier.
 */
export type SubgraphId = 'ferp-core' | 'fhir' | 'payroll' | 'ofbiz';

/**
 * Compliance frameworks applicable to entities.
 * Used for regulatory traceability and audit requirements.
 */
export type ComplianceFramework = 'HIPAA' | 'SOX' | 'PCI' | 'GDPR';

/**
 * Unified status for cross-domain entity views.
 * Maps domain-specific statuses to common semantic states.
 */
export enum UnifiedStatus {
  /** Entity is waiting for action */
  PENDING = 'PENDING',
  /** Entity is currently being processed */
  ACTIVE = 'ACTIVE',
  /** Entity has finished successfully */
  COMPLETED = 'COMPLETED',
  /** Entity encountered an error */
  FAILED = 'FAILED',
  /** Entity was cancelled by user */
  CANCELLED = 'CANCELLED',
  /** Entity is blocked by safety/compliance check */
  BLOCKED = 'BLOCKED',
}

/**
 * Type-safe normalized entity wrapper.
 *
 * @template T - The domain-specific type (e.g., Patient, PayrollRun, Invoice)
 *
 * @example
 * ```typescript
 * // Normalizing a FHIR Patient
 * const normalized: NormalizedEntity<Patient> = {
 *   id: patient.id,
 *   displayName: formatHumanName(patient.name?.[0]),
 *   sourceSubgraph: 'fhir',
 *   sourceType: 'Patient',
 *   safetyLevel: SafetyLevel.CRITICAL,
 *   requiresDualApproval: true,
 *   complianceFrameworks: ['HIPAA'],
 *   unifiedStatus: UnifiedStatus.ACTIVE,
 *   isBlockedBySafety: false,
 *   domainData: patient,  // Full Patient type preserved
 *   normalizedAt: new Date().toISOString(),
 * };
 *
 * // Access domain data with full type safety
 * const birthDate = normalized.domainData.birthDate;  // string, not unknown
 * ```
 */
export interface NormalizedEntity<T = never> {
  // ============================================================================
  // IDENTITY
  // ============================================================================

  /** Unique identifier (Snowflake ID from backend or subgraph ID) */
  readonly id: string;

  /** Human-readable display name for UI */
  readonly displayName: string;

  // ============================================================================
  // SOURCE TRACKING (Audit Trail Support)
  // ============================================================================

  /** Which subgraph this entity originated from */
  readonly sourceSubgraph: SubgraphId;

  /** The type name within the source subgraph (e.g., 'Patient', 'PayrollRun') */
  readonly sourceType: string;

  // ============================================================================
  // LIFE-SAFETY REQUIREMENTS (REQUIRED, not optional)
  // ============================================================================

  /**
   * Safety classification for this entity.
   * REQUIRED - can never be undefined for life-safety enforcement.
   */
  readonly safetyLevel: SafetyLevel;

  /**
   * Whether this entity requires dual approval for modifications.
   * Typically true for LIFE_CRITICAL and CRITICAL safety levels.
   */
  readonly requiresDualApproval: boolean;

  /**
   * Applicable compliance frameworks (HIPAA, SOX, PCI, GDPR).
   * Used for regulatory traceability and audit requirements.
   */
  readonly complianceFrameworks: readonly ComplianceFramework[];

  // ============================================================================
  // STATUS WITH SAFETY SEMANTICS
  // ============================================================================

  /**
   * Unified status for cross-domain views.
   * Maps domain-specific statuses to common semantic states.
   */
  readonly unifiedStatus: UnifiedStatus;

  /**
   * Whether this entity is currently blocked by a safety check.
   * True if safety checks prevent the current action.
   */
  readonly isBlockedBySafety: boolean;

  // ============================================================================
  // TYPE-SAFE DOMAIN DATA
  // ============================================================================

  /**
   * The original domain-specific data with full type preservation.
   * Generic T ensures type safety is maintained through normalization.
   *
   * @example
   * ```typescript
   * const normalized: NormalizedEntity<Patient> = ...;
   * const patient: Patient = normalized.domainData;  // Fully typed
   * ```
   */
  readonly domainData: T;

  // ============================================================================
  // AUDIT METADATA
  // ============================================================================

  /** ISO timestamp when this entity was normalized */
  readonly normalizedAt: string;
}

/**
 * Type guard to check if a value is a NormalizedEntity.
 */
export function isNormalizedEntity<T>(value: unknown): value is NormalizedEntity<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'sourceSubgraph' in value &&
    'safetyLevel' in value &&
    'domainData' in value
  );
}

/**
 * Helper to check if an entity requires dual approval.
 */
export function requiresDualApproval(entity: Pick<NormalizedEntity, 'safetyLevel' | 'requiresDualApproval'>): boolean {
  return entity.requiresDualApproval;
}

/**
 * Helper to check if an entity is life-critical.
 */
export function isLifeCritical(entity: Pick<NormalizedEntity, 'safetyLevel'>): boolean {
  // Import SafetyLevel dynamically to avoid circular dependency
  // In actual usage, compare against SafetyLevel.LIFE_CRITICAL
  return entity.safetyLevel === 'LIFE_CRITICAL';
}

/**
 * Helper to check if an entity is blocked by safety.
 */
export function isBlockedBySafety(entity: Pick<NormalizedEntity, 'isBlockedBySafety'>): boolean {
  return entity.isBlockedBySafety;
}

/**
 * Status mapping utilities for different subgraphs.
 * These will be expanded as normalizers are implemented.
 */
export const STATUS_MAPPINGS = {
  /**
   * FHIR EncounterStatus to UnifiedStatus
   */
  fhirEncounter: {
    planned: UnifiedStatus.PENDING,
    arrived: UnifiedStatus.PENDING,
    triaged: UnifiedStatus.ACTIVE,
    'in-progress': UnifiedStatus.ACTIVE,
    onleave: UnifiedStatus.ACTIVE,
    finished: UnifiedStatus.COMPLETED,
    cancelled: UnifiedStatus.CANCELLED,
    'entered-in-error': UnifiedStatus.FAILED,
    unknown: UnifiedStatus.PENDING,
  } as const,

  /**
   * Payroll PayrollRunStatus to UnifiedStatus
   */
  payrollRun: {
    draft: UnifiedStatus.PENDING,
    processing: UnifiedStatus.ACTIVE,
    processed: UnifiedStatus.COMPLETED,
    paid: UnifiedStatus.COMPLETED,
    archived: UnifiedStatus.COMPLETED,
    failed: UnifiedStatus.FAILED,
    held: UnifiedStatus.BLOCKED,
  } as const,

  /**
   * OFBiz InvoiceStatus to UnifiedStatus
   */
  ofbizInvoice: {
    draft: UnifiedStatus.PENDING,
    submitted: UnifiedStatus.ACTIVE,
    approved: UnifiedStatus.ACTIVE,
    posted: UnifiedStatus.COMPLETED,
    paid: UnifiedStatus.COMPLETED,
    voided: UnifiedStatus.CANCELLED,
    'write-off': UnifiedStatus.FAILED,
  } as const,
} as const;

/**
 * Type-safe status mapping function.
 */
export function mapStatus<T extends keyof typeof STATUS_MAPPINGS>(
  subgraph: T,
  status: keyof (typeof STATUS_MAPPINGS)[T]
): UnifiedStatus {
  const mapping = STATUS_MAPPINGS[subgraph];
  return (mapping as Record<string, UnifiedStatus>)[status as string] ?? UnifiedStatus.PENDING;
}
